import { useEffect, type ReactNode } from "react";

interface BrandShellProps {
  /** Right-aligned content in the header (e.g. a Sign in link). Optional. */
  headerRight?: ReactNode;
  /** Centred body content. */
  children: ReactNode;
}

/**
 * The shared "dark canvas + radial glow + brand mark" shell used by every
 * unauthenticated, full-bleed surface (the marketing root, the sign-in
 * screen, anything else that should sit outside the AppShell).
 *
 * Forces dark theme on `<html>` while mounted so the brand surface stays
 * consistent regardless of the user's preference, restoring on unmount.
 *
 * Fixed-position so the shell always fills the viewport edge-to-edge,
 * regardless of whatever might be wrapping the Inertia page.
 */
export function BrandShell({ headerRight, children }: BrandShellProps) {
  useEffect(() => {
    const html = document.documentElement;
    const previous = html.getAttribute("data-theme");
    html.setAttribute("data-theme", "dark");
    return () => {
      if (previous) html.setAttribute("data-theme", previous);
      else          html.removeAttribute("data-theme");
    };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col overflow-auto bg-canvas text-fg">
      {/* Primary radial glow — sits centred behind the body content. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center"
      >
        <div
          className="h-[900px] w-[900px] max-w-none opacity-70 blur-3xl"
          style={{
            background:
              "radial-gradient(closest-side, var(--accent-9) 0%, transparent 70%)",
          }}
        />
      </div>

      {/* Secondary cooler glow off-centre for depth. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-32 -right-32 z-0 size-[480px] opacity-40 blur-3xl"
        style={{
          background:
            "radial-gradient(closest-side, var(--accent-7) 0%, transparent 70%)",
        }}
      />

      {/* Header: brand mark left, optional slot right. */}
      <header className="relative z-10 flex shrink-0 items-center justify-between px-6 py-5 sm:px-10">
        <a href="https://stabiliti.io" className="flex items-center gap-2 text-fg" aria-label="stabiliti home">
          <img src="/brand-mark.svg" alt="" className="size-7 rounded-[var(--radius-sm)]" />
          <span
            className="text-base font-semibold tracking-tight"
            style={{ fontFamily: "var(--font-brand)" }}
          >
            stabiliti
          </span>
        </a>
        {headerRight && <div className="flex items-center gap-3">{headerRight}</div>}
      </header>

      {/* Centred body — fills the remaining viewport height. */}
      <main className="relative z-10 flex flex-1 items-center justify-center px-6 py-10 sm:px-10">
        {children}
      </main>

      <footer className="relative z-10 shrink-0 px-6 py-5 text-center text-xs text-fg-subtle sm:px-10">
        &copy; {new Date().getFullYear()} stabiliti
      </footer>
    </div>
  );
}
